import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import type { AssetResponse } from "../generated/api/types.gen";
import type {
  AddAssetPromptState,
  PendingAssetImage,
} from "../hooks/useAddAssetPrompt";
import { createImageAssetForProject } from "../lib/asset-create";
import { AssetNameField } from "./AssetNameField";
import { ModalPrimaryButton, ModalSecondaryButton } from "./Button";
import { ASSET_MODAL_CARD, ModalCloseButton, ModalShell } from "./ModalShell";

/** The file's own name is the obvious first suggestion; the extension is noise
 *  in a library listing. */
function defaultAssetName(fileName: string): string {
  return fileName.replace(/\.[^./\\]+$/, "");
}

function AddAsAssetForm({
  pending,
  projectId,
  onClose,
}: {
  pending: PendingAssetImage;
  projectId: string | null | undefined;
  onClose: () => void;
}) {
  const titleId = useId();
  const [name, setName] = useState(() => defaultAssetName(pending.file.name));
  const queryClient = useQueryClient();
  // The upload is not undone when filing the asset into the project fails, so
  // a retry re-files the asset it already made rather than uploading a second.
  const created = useRef<AssetResponse | null>(null);

  const save = useMutation({
    mutationFn: () =>
      createImageAssetForProject(queryClient, {
        file: pending.file,
        name: name.trim(),
        projectId,
        created,
      }),
    onSuccess: onClose,
  });

  const isDisabled = save.isPending || name.trim().length === 0;
  const handleSave = () => {
    if (!isDisabled) save.mutate();
  };

  return (
    <ModalShell
      onClose={onClose}
      ariaContext="add as asset"
      labelledBy={titleId}
      cardClassName={ASSET_MODAL_CARD}
      closeAffordance="none"
    >
      <div className="flex items-center gap-4 px-4 py-6">
        <h2
          id={titleId}
          className="flex-1 text-[18px] font-bold leading-5 text-[var(--text-primary)]"
        >
          Add as Asset
        </h2>
        <ModalCloseButton
          onClose={onClose}
          ariaContext="add as asset"
          size={16}
        />
      </div>

      <div className="px-4">
        <img
          src={pending.previewUrl}
          alt={pending.file.name}
          className="h-[196px] w-full rounded-lg object-cover"
        />
        <div className="mt-4">
          <AssetNameField
            label="File Name"
            value={name}
            onChange={setName}
            onSubmit={handleSave}
          />
        </div>
      </div>

      {save.isError && (
        <p className="px-4 pt-4 text-sm text-red-400" role="alert">
          Couldn&apos;t save this asset. Please try again.
        </p>
      )}

      <div className="flex items-center justify-end gap-4 px-4 pb-6 pt-4">
        <ModalSecondaryButton onClick={onClose} disabled={save.isPending}>
          Skip
        </ModalSecondaryButton>
        <ModalPrimaryButton onClick={handleSave} disabled={isDisabled}>
          {save.isPending ? "Adding…" : "Add"}
        </ModalPrimaryButton>
      </div>
    </ModalShell>
  );
}

/** Offers a just-attached reference image to the asset library. Mounted only
 *  while there is something to offer, so each prompt starts from a clean name
 *  and a clean mutation. */
export function AddAsAssetModal({ prompt }: { prompt: AddAssetPromptState }) {
  if (!prompt.pending) return null;

  return (
    <AddAsAssetForm
      // A second attach while the prompt is open replaces the subject, and the
      // name field has to follow it rather than keep the first file's name.
      key={prompt.pending.previewUrl}
      pending={prompt.pending}
      projectId={prompt.projectId}
      onClose={prompt.dismiss}
    />
  );
}
